import { useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Stack,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Card,
    CardContent,
    CardActions,
    Divider,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import apiRequest from "../../../apiRequest";

import PageLoader from "../../../components/PageLoader/PageLoader";
import AppSnackbar from "../../../components/AppSnackbar/AppSnackbar";
import ConfirmDialog from "components/ConfirmDialog/ConfirmDialog";

const ViewModal = ({ open, onClose, data, actions, onRefresh }) => {
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [reason, setReason] = useState("");

    const [loading, setLoading] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmData, setConfirmData] = useState(null);

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleActionClick = (action) => {
        const label = action.label.toLowerCase();

        if (label === "rejected" || label === "request_info") {
            setSelectedAction(action);
            setReason("");
            setReasonDialogOpen(true);
            return;
        }

        if (label === "approved") {
            // Open confirm dialog for approval
            setConfirmData({ id: data.id, action: label });
            setConfirmOpen(true);
        }
    };

    const handleConfirmApprove = async () => {
        if (!confirmData) return;
        setLoading(true);
        try {
            const payload = {
                business_id: confirmData.id,
                status: "approved",
                reason: "Approved by admin",
            };
            const res = await apiRequest.put("/v1/admin/business/status", payload);

            if (res?.success) {
                showSnackbar("Approved successfully", "success");
                onRefresh?.();
                onClose?.();
            } else {
                showSnackbar(res?.message || "Approval failed", "error");
            }
        } catch (error) {
            showSnackbar("Something went wrong", "error");
        } finally {
            setLoading(false);
            setConfirmOpen(false);
            setConfirmData(null);
        }
    };

    const handleSubmitReason = async () => {
        if (!selectedAction) return;
        setLoading(true);

        const payload = {
            business_id: data.id,
            status: selectedAction.label,
            reason: reason.trim(),
        };

        try {
            const res = await apiRequest.put("/v1/admin/business/status", payload);
            if (res?.success) {
                showSnackbar(`${selectedAction.label} successfully`, "success");
                setReasonDialogOpen(false);
                onRefresh?.();
                onClose?.();
            } else {
                showSnackbar(res?.message || "Action failed", "error");
            }
        } catch (err) {
            showSnackbar("Something went wrong", "error");
        } finally {
            setLoading(false);
            setSelectedAction(null);
            setReason("");
        }
    };

    const getButtonTextColor = (label) => {
        switch (label.toLowerCase()) {
            case "approved":
                return "#2e7d32";
            case "rejected":
                return "#d32f2f";
            case "request_info":
                return "#ed6c02";
            default:
                return "#1976d2";
        }
    };

    const getButtonHoverColor = (label) => {
        switch (label.toLowerCase()) {
            case "approved":
                return "#1b5e20";
            case "rejected":
                return "#b71c1c";
            case "request_info":
                return "#e65100";
            default:
                return "#115293";
        }
    };

    return (
        <>
            {/* MAIN MODAL */}
            <Modal open={open} onClose={onClose}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 780,
                        height: "90vh",
                        maxHeight: "90vh",
                        overflow: "hidden",
                    }}
                >
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: 8,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <IconButton
                            onClick={onClose}
                            sx={{ position: "absolute", top: 8, right: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {data && (
                            <CardContent
                                sx={{
                                    p: 0,
                                    flex: 1,
                                    overflowY: "auto",
                                }}
                            >
                                {/* HEADER */}
                                <Box
                                    sx={{
                                        px: 4,
                                        py: 3,
                                        background:
                                            "linear-gradient(120deg, #dff7e0 0%, #f4fff7 100%)",
                                        borderBottom: "1px solid #e5e7eb",
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Business Overview
                                    </Typography>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: "flex-start", sm: "center" }}
                                        spacing={2}
                                        mt={1}
                                    >
                                        <Box>
                                            <Typography variant="h5" fontWeight={700}>
                                                {data.name || "N/A"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {data.category?.name_en || "No category"}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <Chip
                                                label={data.is_registered_business ? "Registered" : "Individual"}
                                                color={data.is_registered_business ? "info" : "warning"}
                                                variant="outlined"
                                            />
                                            <Chip
                                                label={data.status || "Status Unknown"}
                                                color={
                                                    data.status === "approved"
                                                        ? "success"
                                                        : data.status === "rejected"
                                                            ? "error"
                                                            : "warning"
                                                }
                                            />
                                        </Stack>
                                    </Stack>
                                </Box>

                                {/* DETAILS */}
                                <Box sx={{ px: 4, py: 3 }}>
                                    {(() => {
                                        const detailItems = [
                                            { label: "Business Name", value: data.name },
                                            { label: "Address", value: data.address },
                                            { label: "Business License Number", value: data.business_license_number },
                                            { label: "Insurance Number", value: data.insurance_number },
                                            {
                                                label: "Insurance Expiry Date",
                                                value: data.insurance_expiry_date
                                                    ? new Date(data.insurance_expiry_date).toLocaleDateString()
                                                    : null,
                                            },
                                            ...(!data.is_registered_business
                                                ? [{ label: "Personal ID Number", value: data.personal_id_number }]
                                                : []),
                                            ...(data.status !== "approved"
                                                ? [
                                                    { label: "Request Info", value: data.request_info },
                                                    { label: "Reason of Rejection", value: data.reason_of_reject },
                                                ]
                                                : []),
                                        ].filter(
                                            (item) =>
                                                item.value !== null &&
                                                item.value !== undefined &&
                                                item.value !== ""
                                        );

                                        return (
                                            <Grid container spacing={2}>
                                                {detailItems.map((item) => (
                                            <Grid item xs={12} sm={6} key={item.label}>
                                                <Box
                                                    sx={{
                                                        border: "1px solid #edf2f7",
                                                        borderRadius: 2,
                                                        p: 2,
                                                        backgroundColor: "#fafafa",
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.label}
                                                    </Typography>
                                                    <Typography fontWeight={600}>
                                                        {item.value || "-"}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                                ))}
                                            </Grid>
                                        );
                                    })()}

                                    <Divider sx={{ my: 3 }} />

                                    {(() => {
                                        const logoDoc = data?.documents?.find(
                                            (doc) => doc?.type === "logo" && doc?.file_path
                                        );
                                        if (!logoDoc) return null;
                                        return (
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    Logo
                                                </Typography>
                                                <Box
                                                    component="img"
                                                    src={`${apiRequest.BASE_URL}/${logoDoc.file_path}`}
                                                    alt="Business logo"
                                                    sx={{
                                                        width: "100%",
                                                        maxWidth: 220,
                                                        height: 140,
                                                        objectFit: "cover",
                                                        borderRadius: 2,
                                                        border: "1px solid #e5e7eb",
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })()}

                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" mb={1}>
                                                Registration Details
                                            </Typography>
                                            <Box
                                                sx={{
                                                    borderRadius: 2,
                                                    border: "1px dashed #cbd5f5",
                                                    p: 2,
                                                    backgroundColor: "#f8fbff",
                                                }}
                                            >
                                                <Stack spacing={1}>
                                                    <Typography variant="body2">
                                                        Registered Business:{" "}
                                                        <Typography component="span" fontWeight={600}>
                                                            {data.is_registered_business ? "Yes" : "No"}
                                                        </Typography>
                                                    </Typography>
                                                    {/* <Typography variant="body2">
                                                        Created At:{" "}
                                                        <Typography component="span" fontWeight={600}>
                                                            {data.createdAt
                                                                ? new Date(data.createdAt).toLocaleDateString()
                                                                : "-"}
                                                        </Typography>
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Updated At:{" "}
                                                        <Typography component="span" fontWeight={600}>
                                                            {data.updatedAt
                                                                ? new Date(data.updatedAt).toLocaleDateString()
                                                                : "-"}
                                                        </Typography>
                                                    </Typography> */}
                                                </Stack>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" mb={1}>
                                                Notes
                                            </Typography>
                                            <Box
                                                sx={{
                                                    borderRadius: 2,
                                                    border: "1px solid #fce7f3",
                                                    p: 2,
                                                    backgroundColor: "#fff5fb",
                                                    minHeight: 120,
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {data.status === "rejected"
                                                        ? "Reason of Rejection"
                                                        : data.status === "request_info"
                                                            ? "Request Info"
                                                            : "Additional Comments"}
                                                </Typography>
                                                <Typography>
                                                    {data.status === "rejected"
                                                        ? data.reason_of_reject || "No reason provided."
                                                        : data.status === "request_info"
                                                            ? data.request_info || "No information requested."
                                                            : data.notes || "No remarks were provided for this business."}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* ACTIONS */}
                                    <Box pb={2} />
                                </Box>
                            </CardContent>
                        )}

                        <CardActions
                            sx={{
                                borderTop: "1px solid #e5e7eb",
                                p: 3,
                                justifyContent: "flex-end",
                                gap: 1.5,
                                flexWrap: "wrap",
                                position: "sticky",
                                bottom: 0,
                                bgcolor: "#fff",
                            }}
                        >
                            {actions.map((action) => (
                                <Button
                                    key={action.label}
                                    variant="outlined"
                                    onClick={() => handleActionClick(action)}
                                    sx={{
                                        minWidth: 140,
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        color: getButtonTextColor(action.label),
                                        borderColor: getButtonTextColor(action.label),
                                        "&:hover": {
                                            bgcolor: "#f5f5f5",
                                            borderColor: getButtonHoverColor(action.label),
                                        },
                                    }}
                                >
                                    {action.label.replace("_", " ").toUpperCase()}
                                </Button>
                            ))}
                        </CardActions>
                    </Card>
                </Box>
            </Modal>

            {/* REASON DIALOG */}
            <Dialog open={reasonDialogOpen} onClose={() => setReasonDialogOpen(false)}>
                <DialogTitle>{selectedAction?.label}</DialogTitle>
                <DialogContent>
                    <TextField
                        multiline
                        rows={4}
                        fullWidth
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReasonDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        // color="error",
                        sx={{
                            borderColor: "#2e7d32",
                            color: "#fcfcfcff",
                        }}
                        disabled={!reason.trim()}
                        onClick={handleSubmitReason}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog >

            {/* CONFIRM DIALOG FOR APPROVE */}
            < ConfirmDialog
                open={confirmOpen}
                title="Approve Driver"
                description={`Are you sure you want to approve this ${data?.name} ?`}
                confirmText="Yes, Approve"
                confirmColor="success"
                loading={loading}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmApprove}
            />

            {/* GLOBAL LOADER */}
            < PageLoader open={loading} />
            <AppSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default ViewModal;
